import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, mergeProps as _mergeProps, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "notification-wrapper" }
const _hoisted_2 = { class: "bell-icon-container" }
const _hoisted_3 = { class: "dropdown-header" }
const _hoisted_4 = { class: "notifications-scroll" }
const _hoisted_5 = {
  key: 0,
  class: "notif-list"
}
const _hoisted_6 = {
  key: 0,
  class: "notifications-scroll__hint",
  "data-testid": "notifications-scroll-hint",
  "aria-hidden": "true"
}
const _hoisted_7 = { class: "dropdown-actions" }

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useNotifications } from '../../../store/notifications';
import NotificationCard from '@/components/notifications/NotificationCard.vue';
import { useNotificationsRealtime } from '@/composables/useNotificationsRealtime';
import type { RawNotification } from '@/shared/types/notification';

const POLL_INTERVAL_MS = 30_000;
// L1 — когда WS не встаёт (realtime.degraded), polling становится единственным
// путём доставки, поэтому ужимаем интервал, ограничивая worst-case свежесть
// колокола (~10 c вместо 30 c). При восстановлении WS возвращаемся к 30 c.
const DEGRADED_POLL_INTERVAL_MS = 10_000;
const JITTER_MS = 5_000;
const DROPDOWN_LIMIT = 8;

export default /*@__PURE__*/_defineComponent({
  __name: 'NotificationComponent',
  emits: ["login-click"],
  setup(__props) {

const rootStore = useStore();
const router = useRouter();
const notifications = useNotifications();
const realtime = useNotificationsRealtime(notifications);



const isAuthenticated = computed(
    () => rootStore.getters.isAuthenticated,
);

const open = ref(false);

let pollTimer: ReturnType<typeof setTimeout> | null = null;
// E04 — гигиена: привязываем HTTP-запросы поллинга к жизненному циклу
// подписок (startSubscriptions/stopSubscriptions), чтобы при unmount/логауте
// in-flight refreshUnreadCount/refreshFromServer отменялись, а не «висели».
let pollController: AbortController | null = null;

// UX 5.2/5.4: один хронологический список вместо деления action/info.
// Стор держит `items` уже отсортированными created_at DESC (инвариант 5.4),
// поэтому здесь НЕ сортируем — просто берём префикс для дропдауна.
// dismiss физически убирает item из стора (5.5), а viewed/acted остаются
// в ленте как «прочитанные».
const dropdownItems = computed<RawNotification[]>(() =>
    // LG-023: feedItems — та же лента, но по одному обращению одна карточка.
    notifications.feedItems.slice(0, DROPDOWN_LIMIT),
);

// ── LG-025: подсказка о прокрутке ────────────────────────────────────────
//
// Третья карточка обрезалась нижним краем панели ровно посередине строки, и
// понять, что список продолжается, было нельзя: полоса прокрутки в этом
// браузерном стиле появляется только при наведении.
const scrollEl = ref<HTMLElement | null>(null);
const hasMoreToScroll = ref(false);

function updateScrollHint(): void {
    const el = scrollEl.value;
    if (!el) {
        hasMoreToScroll.value = false;
        return;
    }
    // Порог в 4px: на дробном devicePixelRatio scrollHeight и clientHeight
    // расходятся на доли пикселя, и градиент мигал бы на конце списка.
    hasMoreToScroll.value = el.scrollHeight - el.scrollTop - el.clientHeight > 4;
}

// Пересчитываем, когда список изменился: пришло новое уведомление, сработал
// dismiss или дропдаун открылся впервые.
watch(
    dropdownItems,
    () => {
        void nextTick(updateScrollHint);
    },
    { immediate: true, deep: false },
);

function schedulePoll() {
    if (typeof document !== 'undefined' && document.hidden) return;
    const base = realtime.degraded.value
        ? DEGRADED_POLL_INTERVAL_MS
        : POLL_INTERVAL_MS;
    const next = base + Math.random() * JITTER_MS;
    pollTimer = setTimeout(async () => {
        pollTimer = null;
        // Когда WS деградировал, polling — единственный путь доставки: тянем
        // count+list (refreshFromServer), чтобы новые карточки появлялись в
        // дропдауне сразу, а не только при ручном открытии (баг #6, ветка
        // fallback). При живом WS хватает дешёвого refreshUnreadCount — список
        // наполняет WS-пуш (pushFromSocket), лишний list-запрос не нужен.
        await pollRefresh();
        schedulePoll();
    }, next);
}

// Свежесть колокола по polling'у: список тянем только на degraded-ветке
// (WS не доставляет), иначе — только счётчик. Держит happy-path дешёвым.
async function pollRefresh(): Promise<void> {
    if (realtime.degraded.value) {
        await notifications.refreshFromServer(pollController?.signal);
    } else {
        await notifications.refreshUnreadCount(pollController?.signal);
    }
}

function clearPoll() {
    if (pollTimer !== null) {
        clearTimeout(pollTimer);
        pollTimer = null;
    }
}

function handleVisibilityChange() {
    if (typeof document === 'undefined') return;
    if (!document.hidden && pollTimer === null) {
        void notifications.refreshUnreadCount(pollController?.signal);
        schedulePoll();
    } else if (document.hidden) {
        clearPoll();
    }
}

function handleWindowFocus() {
    void notifications.refreshUnreadCount(pollController?.signal);
}

function handleTokenRefresh() {
    void notifications.refreshUnreadCount(pollController?.signal);
}

// Доставка уведомлений в колокол — ДВА независимых пути:
//   1) realtime WS-пуш (useNotificationsRealtime → singleton /ws/notifications/):
//      бэк бродкастит каждое платформенное уведомление, и оно появляется в
//      колоколе БЕЗ перезагрузки страницы (баги КУ #29/#30/#99). Сокет — один
//      на весь app-session (см. useNotificationSocket), поэтому прежнее
//      возражение «по коннекту на КАЖДОЙ странице, слишком жирно» снято:
//      ChatsPage и колокол делят один сокет.
//   2) polling раз в 30 сек + jitter (schedulePoll) — safety-net на случай,
//      если WS оборвался / прокся гасит upgrade / cookie протух.
//
// Гейтим оба пути на isAuthenticated, чтобы гости не открывали сокет и не
// ловили 401-шум на /unread_count/.
function startSubscriptions() {
    realtime.start();
    if (pollTimer !== null) return; // polling уже идёт
    pollController = typeof AbortController !== 'undefined' ? new AbortController() : null;
    void notifications.refreshUnreadCount(pollController?.signal);
    schedulePoll();
}

function stopSubscriptions() {
    realtime.stop();
    clearPoll();
    // E04 — гасим ещё не завершившийся poll-запрос (unmount/логаут), чтобы
    // ответ не прилетел в стор уже неактуальным.
    pollController?.abort();
    pollController = null;
}

onMounted(() => {
    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', handleVisibilityChange);
    }
    if (typeof window !== 'undefined') {
        window.addEventListener('focus', handleWindowFocus);
        window.addEventListener('jwt-refreshed', handleTokenRefresh);
    }
});

watch(
    isAuthenticated,
    (authed) => {
        if (authed) {
            startSubscriptions();
        } else {
            stopSubscriptions();
        }
    },
    { immediate: true },
);

// L1 — при деградации WS немедленно переходим на учащённый polling (а не ждём
// конца текущего 30-секундного таймера); при восстановлении следующий цикл сам
// возьмёт расслабленный интервал.
watch(
    () => realtime.degraded.value,
    (isDegraded) => {
        if (!isAuthenticated.value) return;
        if (isDegraded && pollTimer !== null) {
            clearPoll();
            // Немедленный catch-up на degraded-ветке — count+list (см. pollRefresh).
            void pollRefresh();
            schedulePoll();
        }
    },
);

onBeforeUnmount(() => {
    stopSubscriptions();
    if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
    if (typeof window !== 'undefined') {
        window.removeEventListener('focus', handleWindowFocus);
        window.removeEventListener('jwt-refreshed', handleTokenRefresh);
    }
});

watch(open, async (isOpen) => {
    if (!isOpen) return;
    if (!notifications.loaded) await notifications.load();
    const pendingIds = notifications.items
        .filter(n => n.status === 'pending')
        .map(n => n.public_id)
        .filter((id): id is string => !!id);
    if (pendingIds.length) await notifications.markViewed(pendingIds);
});

async function onAct(publicId: string, href: string) {
    await notifications.act(publicId);
    open.value = false;
    if (href) router.push(href);
}

async function onDismiss(publicId: string) {
    await notifications.dismissGroup(publicId);
}

function onOpenItem(publicId: string) {
    open.value = false;
    router.push({ path: '/notifications', query: { highlight: publicId } });
}

function onOpenAll() {
    open.value = false;
    router.push('/notifications');
}

return (_ctx: any,_cache: any) => {
  const _component_v_badge = _resolveComponent("v-badge")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_empty_state = _resolveComponent("v-empty-state")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_menu = _resolveComponent("v-menu")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!isAuthenticated.value)
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          class: "login-button",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('login-click')))
        }, " Войти "))
      : (_openBlock(), _createBlock(_component_v_menu, {
          key: 1,
          modelValue: open.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((open).value = $event)),
          "close-on-content-click": false,
          location: "bottom end",
          offset: "8",
          transition: "fade-transition",
          "max-width": "min(440px, calc(100vw - 32px))"
        }, {
          activator: _withCtx(({ props: activator }) => [
            _createVNode(_component_v_btn, _mergeProps({
              icon: "",
              variant: "text",
              class: "notification-trigger"
            }, activator, {
              "aria-label": `Уведомления (${_unref(notifications).unreadCount})`
            }), {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_2, [
                  _cache[2] || (_cache[2] = _createElementVNode("svg", {
                    width: "22",
                    height: "26",
                    viewBox: "0 0 22 26",
                    fill: "none",
                    xmlns: "http://www.w3.org/2000/svg",
                    class: "bell-icon"
                  }, [
                    _createElementVNode("path", {
                      d: "M14.6666 19.1111H20.7777L19.0606 17.394C18.5949 16.9283 18.3333 16.2967 18.3333 15.6381V11.7778C18.3333 8.5848 16.2926 5.86844 13.4444 4.86173V4.44444C13.4444 3.09442 12.35 2 10.9999 2C9.64992 2 8.5555 3.09441 8.5555 4.44444V4.86173C5.70725 5.86844 3.66661 8.5848 3.66661 11.7778V15.6381C3.66661 16.2967 3.40499 16.9283 2.93931 17.394L1.22217 19.1111H7.33328M14.6666 19.1111V20.3333C14.6666 22.3584 13.025 24 10.9999 24C8.9749 24 7.33328 22.3584 7.33328 20.3333V19.1111M14.6666 19.1111H7.33328",
                      stroke: "currentColor",
                      "stroke-width": "2.04545",
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round"
                    })
                  ], -1)),
                  (_unref(notifications).unreadCount)
                    ? (_openBlock(), _createBlock(_component_v_badge, {
                        key: 0,
                        content: _unref(notifications).unreadCount > 99 ? '99+' : _unref(notifications).unreadCount,
                        color: "error",
                        class: "notification-badge"
                      }, null, 8, ["content"]))
                    : _createCommentVNode("", true)
                ])
              ]),
              _: 2
            }, 1040, ["aria-label"])
          ]),
          default: _withCtx(() => [
            _createVNode(_component_v_card, {
              class: "notification-dropdown",
              elevation: "8",
              rounded: 12
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_3, [
                  _cache[3] || (_cache[3] = _createElementVNode("h3", { class: "header-title" }, "Уведомления", -1)),
                  (_unref(notifications).unreadCount)
                    ? (_openBlock(), _createBlock(_component_v_chip, {
                        key: 0,
                        size: "small",
                        color: "primary",
                        variant: "flat",
                        class: "unread-chip"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(notifications).unreadCount) + " новых ", 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ]),
                _createVNode(_component_v_divider, { class: "header-divider" }),
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("div", {
                    ref_key: "scrollEl",
                    ref: scrollEl,
                    class: "notifications-container",
                    onScroll: updateScrollHint
                  }, [
                    (dropdownItems.value.length)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dropdownItems.value, (n) => {
                            return (_openBlock(), _createBlock(NotificationCard, {
                              key: n.public_id,
                              notification: n,
                              onAct: onAct,
                              onDismiss: onDismiss,
                              onOpen: onOpenItem
                            }, null, 8, ["notification"]))
                          }), 128))
                        ]))
                      : (_openBlock(), _createBlock(_component_v_empty_state, {
                          key: 1,
                          icon: "mdi-bell-check-outline",
                          title: "Пока тихо",
                          text: "Здесь появятся новые уведомления.",
                          class: "notif-empty"
                        }))
                  ], 544),
                  (hasMoreToScroll.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_6))
                    : _createCommentVNode("", true)
                ]),
                _createVNode(_component_v_divider, { class: "header-divider" }),
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(_component_v_btn, {
                    variant: "text",
                    size: "small",
                    color: "primary",
                    class: "action-button",
                    onClick: onOpenAll
                  }, {
                    default: _withCtx(() => _cache[4] || (_cache[4] = [
                      _createTextVNode(" Все уведомления ")
                    ])),
                    _: 1
                  })
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue"]))
  ]))
}
}

})